import React, { FC, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { useToasts } from 'react-toast-notifications';

import { Button, LoaderSpinner } from 'components/UtilElements';
import { apiErrorHandler, toastHookToContext } from 'core/errors';
import { Dispatch } from 'redux';
import { UPDATE_COURSE_TIMELINE, UPDATE_ENROLLMENTS } from 'redux/actions';
import { API, request } from 'redux/api';
import { RootState } from 'redux/reducers';

import CreateClassDialog from './CreateClassDialog';
import TimelineWidget from './TimelineWidget';

interface SaveCourseTimelineResponse {
  message: string;
  enrolments: {
    students: CourseUser[];
    importStatus: ImportStatus;
    totalStudents: number;
    studentsImported: number;
  };
}

const toRawCourseTimelineClass = (t: CourseTimelineClass): RawCourseTimelineClass => ({
  id: t.id,
  startTime: t.startTime,
  startTimeUnix: t.startTimeUnix,
  endTime: t.endTime,
  endTimeUnix: t.endTimeUnix,
  venue: t.venue,
  isOnlineMeeting: t.isOnlineMeeting,
});

const saveCourseTimeline = (
  courseId: string,
  newClasses: CourseTimelineClass[],
  removedClasses: CourseTimelineClass[]
) => {
  return request<SaveCourseTimelineResponse>({
    method: 'PATCH',
    url: API.COURSE_CREATE_SAVE_TIMELINE,
    data: { courseId, newClasses, removedClasses },
  });
};

interface CourseTimelineProps {
  course: CourseDetails | null;
  timeline: CourseTimelineClass[];
  onNext(): void;
  updateTimeline(data: CourseTimelineClass[]): Promise<void>;
  updateEnrollments(data: CourseEnrollments): Promise<void>;
}

const CourseTimeline: FC<CourseTimelineProps> = ({
  course,
  timeline,
  onNext,
  updateTimeline,
  updateEnrollments,
}) => {
  const tm = useToasts();
  const [isSaving, setIsSaving] = useState(false);
  const [showAddClassDialog, setShowAddClassDialog] = useState(false);

  const onClassToggle = useCallback(
    (id: string) =>
      updateTimeline(timeline.map((c) => (c.id === id ? { ...c, isRemoved: !c.isRemoved } : c))),
    [timeline, updateTimeline]
  );

  const onCloseAddClassDialog = useCallback(
    (newClass?: CourseTimelineClass) => {
      /**
       * newClass can be CourseTimelineClass object or SyntheticBaseEvent
       * so this condition narrow down to CourseTimelineClass
       */
      if (newClass && typeof newClass === 'object' && 'id' in newClass) {
        const isDuplicate = timeline.find((item) => item.id === newClass?.id);
        if (newClass && !isDuplicate) {
          const updatedTimeline = timeline.concat(newClass).sort((t1, t2) => {
            if (t2.id > t1.id) return -1;
            if (t2.id < t1.id) return 1;
            return 0;
          });
          updateTimeline(updatedTimeline);
        }
      }

      setShowAddClassDialog(false);
    },
    [timeline, updateTimeline]
  );

  const onSubmit = useCallback(async () => {
    if (!course) return;
    try {
      setIsSaving(true);
      const newClasses = timeline.filter((t) => t.isNew && !t.isRemoved).map(toRawCourseTimelineClass);
      const removedClasses = timeline.filter((t) => !t.isNew && t.isRemoved).map(toRawCourseTimelineClass);
      const { enrolments } = await saveCourseTimeline(course.courseId, newClasses, removedClasses);
      updateEnrollments(enrolments);
      onNext();
    } catch (err) {
      setIsSaving(false);
      apiErrorHandler(toastHookToContext(tm), err);
    }
  }, [course, onNext, timeline, tm, updateEnrollments]);

  return (
    <>
      <form className='import-course__form with-actions'>
        <div className='import-course__form-content'>
          <label className='import-course__label'>Timeline</label>
          {timeline.map((t) => (
            <TimelineWidget key={t.id} {...t} onClassToggle={onClassToggle} />
          ))}
        </div>
        <div className='import-course__form-actions'>
          <Button
            label='Add a Class'
            type='secondary'
            selector='btn-colored btn-large btn-block'
            onRipple={() => setShowAddClassDialog(true)}
          />
        </div>
      </form>
      <section className='import-course__info'>
        <h2 className='import-course__title'>Step 3: Curate course timeline</h2>
        <p className='import-course__description'>
          You can add or remove classes that do not follow the normal weekly schedule
        </p>
        <div className='import-course__actions'>
          <Button
            type='colored'
            disabled={isSaving}
            selector={`btn-primary btn-large  btn-save-continue`}
            onRipple={onSubmit}
          >
            {isSaving ? <LoaderSpinner /> : 'Save and Continue'}
          </Button>
        </div>
      </section>
      <CreateClassDialog course={course} show={showAddClassDialog} onClose={onCloseAddClassDialog} />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  course: state.courseInCreation,
  timeline: state.timeline,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateTimeline: async (data: CourseTimelineClass[]) => {
    dispatch({
      type: UPDATE_COURSE_TIMELINE,
      payload: data,
    });
  },
  updateEnrollments: async (data: CourseEnrollments) => {
    dispatch({
      type: UPDATE_ENROLLMENTS,
      payload: data,
    });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CourseTimeline);
